import { defineStore } from "pinia";
import { ref, type Ref } from "vue";
import { api } from "@/app/services/api.service";
import { BusinessAccount, MDAAccount, UserAccount } from "../types";
import type { OBRSPaginatedApiResponse } from "@/app/types";
import { AxiosResponse } from "axios";

export const useAccountStore = defineStore("accounts", () => {
  const pagination: Ref<OBRSPaginatedApiResponse<any> | null> = ref(null);
  const officer_accounts: Ref<UserAccount[] | null> = ref(null);
  const mda_accounts: Ref<MDAAccount[] | null> = ref(null);
  const private_organisation_accounts: Ref<MDAAccount[] | null> = ref(null);
  const user_accounts: Ref<UserAccount[] | null> = ref(null);
  const business_accounts: Ref<BusinessAccount[] | null> = ref(null);
  const individual_accounts_count: Ref<number> = ref(0);
  const partner_accounts_count: Ref<number> = ref(0);
  const mda_accounts_count: Ref<number> = ref(0);
  const private_organisations_accounts_count: Ref<number> = ref(0);
  const business_accounts_count: Ref<number> = ref(0);
  const client_accounts_count: Ref<number> = ref(0);
  const officer_accounts_count: Ref<number> = ref(0);
  const user_details: Ref<Record<string, any>> = ref({});
  const user_error: Ref<any> = ref(null);
  const reactivationSuccess: Ref<any> = ref(null);
  const reactivationError: Ref<any> = ref(null);
  const idAvailability: Ref<any> = ref(null);
  const ninInfo: Ref<any> = ref(null);
  const ninError: Ref<any> = ref(null);
  const idAttachment: Ref<any> = ref(null);
  const idAttachmentError: Ref<any> = ref(null);
  const signatureUpdate: Ref<any> = ref(null);
  const signatureUpdateError: Ref<any> = ref(null);
  const signatureDetails: Ref<any> = ref(null);
  const signatureDetailsError: Ref<any> = ref(null);
  const signatureRemove: Ref<any> = ref(null);
  const signatureRemoveError: Ref<any> = ref(null);
  const clients: Ref<any> = ref(null);

  const fetchUsers = async (queryString: string) => {
    return api().get("auth/api/users?" + queryString)
      .then((response: AxiosResponse<UserAccount[]>) => {
        officer_accounts.value = response.data;
      });
  };

  const fetchMDAAccounts = async (queryString: string) => {
    return api().get("auth/api/users?" + queryString)
      .then((response: AxiosResponse<MDAAccount[]>) => {
        mda_accounts.value = response.data;
        mda_accounts_count.value = response.data.length;
      });
  };

  const fetchPrivateOrganisationAccounts = async (queryString: string) => {
    return api().get("auth/api/users?" + queryString)
      .then((response: AxiosResponse<MDAAccount[]>) => {
        private_organisation_accounts.value = response.data;
        private_organisations_accounts_count.value = response.data.length;
      });
  };

  const fetchStaffAccounts = async (queryString: string) => {
    return api().get("auth/api/users/staff?" + queryString)
      .then((response: AxiosResponse<OBRSPaginatedApiResponse<UserAccount>>) => {
        officer_accounts.value = response.data.data.content;
        officer_accounts_count.value = response.data.data.totalElements;
        pagination.value = response.data;
      });
  };

  const fetchBusinessAccounts = async (queryString: string) => {
    return api().get("auth/api/users/business?" + queryString)
      .then((response: AxiosResponse<OBRSPaginatedApiResponse<BusinessAccount>>) => {
        business_accounts.value = response.data.data.content;
        business_accounts_count.value = response.data.data.totalElements;
        pagination.value = response.data;
      });
  };

  const fetchClientAccounts = async (queryString: string) => {
    return api().get("auth/api/users/individual?" + queryString)
      .then((response: AxiosResponse<OBRSPaginatedApiResponse<UserAccount>>) => {
        user_accounts.value = response.data.data.content;
        client_accounts_count.value = response.data.data.totalElements;
        pagination.value = response.data;
      });
  };

  const fetchPartners = async (type: string) => {
    return api().get("auth/api/partners?partner-type=" + type)
      .then((response) => {
        user_accounts.value = response.data;
      })
      .catch((error) => {
        user_error.value = error.response?.data;
      });
  };

  const fetchUserDetails = async (userId: string | number) => {
    return api().get("auth/api/users/" + userId)
      .then((response) => {
        user_details.value = response.data;
      })
      .catch((error) => {
        user_error.value = error.response?.data;
      });
  };

  const doResendActivationLink = async (data: any) => {
    return api().post("/auth/api/auth/send-reactivation-link", data)
      .then((response) => {
        reactivationSuccess.value = response.data;
      })
      .catch((error) => {
        reactivationError.value = error.response?.data;
      });
  };

  const changeAccountStatus = async (data: any) => {
    return api().post("/auth/api/users/update-account-status", data)
      .then((response) => {
        reactivationSuccess.value = response.data;
      })
      .catch((error) => {
        reactivationError.value = error.response?.data;
      });
  };

  const validateNin = async (data: any) => {
    return api("https://nira.ursbonline.go.ug").post("/api/nira", data)
      .then((response) => {
        ninInfo.value = response.data;
      })
      .catch((error) => {
        ninError.value = error.response?.data;
      });
  };

  const checkIdAvailability = async (data: any) => {
    return api().post("/auth/api/users/checkIdNumberAvailability", data)
      .then((response) => {
        idAvailability.value = response.data;
      })
      .catch((error) => {
        user_error.value = error.response?.data;
      });
  };

  const updateStaffDepartment = async (data: any) => {
    return api().post("/auth/api/users/update-staff-department", data)
      .then((response) => {
        reactivationSuccess.value = response.data;
      })
      .catch((error) => {
        reactivationError.value = error.response?.data;
      });
  };

  const updateStaffContacts = async (data: any) => {
    return api().post("/auth/api/users/update-user", data)
      .then((response) => {
        reactivationSuccess.value = response.data;
      })
      .catch((error) => {
        reactivationError.value = error.response?.data;
      });
  };

  const updateAccountContactDetails = async (data: any) => {
    return api().post("/auth/api/partner/update-contact-person", data)
      .then((response) => {
        reactivationSuccess.value = response.data;
      })
      .catch((error) => {
        reactivationError.value = error.response?.data;
      });
  };

  const fetchIdAttachment = async (userId: string | number) => {
    return api().get("auth/api/users/user-attachment/" + userId)
      .then((response) => {
        idAttachment.value = response.data;
      })
      .catch((error) => {
        idAttachmentError.value = error.response?.data;
      });
  };

  const doUpdateStaffSignature = async (data: any) => {
    signatureUpdate.value = null;
    signatureUpdateError.value = null;
    const config = data instanceof FormData 
      ? { headers: { 'Content-Type': 'multipart/form-data' } } 
      : {}
    return api().post("/auth/api/users/staff-signature/update", data, config)
      .then((response) => {
        signatureUpdate.value = response.data;
      })
      .catch((error) => {
        signatureUpdateError.value = error.response?.data;
      });
  };

  const fetchStaffSignature = async (data: { id: string | number }) => {
    return api().get("/auth/api/users/staff-signature/" + data.id)
      .then((response) => {
        signatureDetails.value = response.data;
      })
      .catch((error) => {
        signatureDetailsError.value = error.response?.data;
      });
  };

  const doRemoveStaffSignature = async (userId: string) => {
    signatureRemove.value = null;
    signatureRemoveError.value = null;
    return api()
      .post(`/auth/api/users/staff-signature-remove/${encodeURIComponent(userId)}`)
      .then((response) => {
        signatureRemove.value = response.data;
      })
      .catch((error) => {
        signatureRemoveError.value = error.response?.data;
      });
  };

  const revalidateNin = async (nin: string) => {
    return api().post("/auth/api/users/nin-revalidate", { nin });
  };

  const searchClients = async (queryParams: string) => {
    return api().get("auth/api/users/individual?" + queryParams)
      .then((response) => {
        clients.value = response.data;
      })
      .catch((error) => {
        user_error.value = error.response?.data;
      });
  };

  return {
    pagination,
    officer_accounts,
    private_organisation_accounts,
    mda_accounts,
    user_accounts,
    business_accounts,
    individual_accounts_count,
    partner_accounts_count,
    mda_accounts_count,
    client_accounts_count,
    private_organisations_accounts_count,
    business_accounts_count,
    officer_accounts_count,
    user_details,
    user_error,
    reactivationSuccess,
    reactivationError,
    idAvailability,
    ninInfo,
    ninError,
    idAttachment,
    idAttachmentError,
    signatureUpdate,
    signatureUpdateError,
    signatureDetails,
    signatureDetailsError,
    signatureRemove,
    signatureRemoveError,
    clients,
    fetchUsers,
    fetchStaffAccounts,
    fetchBusinessAccounts,
    fetchClientAccounts,
    fetchPartners,
    fetchUserDetails,
    doResendActivationLink,
    changeAccountStatus,
    validateNin,
    checkIdAvailability,
    updateStaffDepartment,
    updateStaffContacts,
    updateAccountContactDetails,
    fetchIdAttachment,
    doUpdateStaffSignature,
    fetchStaffSignature,
    doRemoveStaffSignature,
    searchClients,
    revalidateNin,
    fetchMDAAccounts,
    fetchPrivateOrganisationAccounts
  };
});
